import {variables as apiVariables} from '@/environment/api';
import {useAuthQuery, useAuthMutation} from '@/features/auth/hooks/use-auth-query';
import {queryClient} from './query-client';

// Query keys for cache invalidation
export const queryKeys = {
  userInfo: ['user-info'],
  organizations: ['organizations'],
  apiKeys: ['api-keys'],
};

// Get the API base URL from environment variables
const getApiBaseUrl = () => apiVariables.RPC_BASE_URL.value;

// Define API hooks using the authenticated query hooks
export const useUserInfoQuery = (options = {}) =>
  useAuthQuery(
    queryKeys.userInfo,
    (token) =>
      fetch(`${getApiBaseUrl()}/api/auth/userinfo`, {
        headers: {Authorization: `Bearer ${token}`},
      }).then((resp) => resp.json()),
    options,
  );

export const useOrganizationsQuery = (options = {}) =>
  useAuthQuery(
    queryKeys.organizations,
    (token) =>
      fetch(`${getApiBaseUrl()}/api/organizations`, {
        headers: {Authorization: `Bearer ${token}`},
      }).then((resp) => resp.json()),
    options,
  );

export const useGenerateAdminPortalLinkMutation = () =>
  useAuthMutation((token, variables: {organization_id: string}) =>
    fetch(`${getApiBaseUrl()}/api/organizations/sso/portal-link`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(variables),
    }).then((resp) => resp.json()),
  );

export const useApiKeysQuery = (options = {}) =>
  useAuthQuery(
    queryKeys.apiKeys,
    (token) =>
      fetch(`${getApiBaseUrl()}/api/apikeys`, {
        headers: {Authorization: `Bearer ${token}`},
      }).then((resp) => resp.json()),
    options,
  );

export const useCreateApiKeyMutation = () =>
  useAuthMutation((token, variables: {name: string}) =>
    fetch(`${getApiBaseUrl()}/api/apikeys`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(variables),
    }).then((resp) => resp.json()),
  );

export const useDeleteApiKeyMutation = () =>
  useAuthMutation((token, keyId: string) =>
    fetch(`${getApiBaseUrl()}/api/apikeys/${keyId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((resp) => {
      if (resp.status === 204) {
        return {success: true};
      }

      return resp.json();
    }),
  );

// Helper function to invalidate queries
export function invalidateQueries(queryKey: unknown[]) {
  return queryClient.invalidateQueries({queryKey});
}
